// Modular Custom Uppy Uploader Component
// This component handles file uploads to our Rails backend, which then uploads to S3
// Follows the pattern from episodes_new_page.js with all Uppy logic outside Alpine

import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import XHRUpload from '@uppy/xhr-upload';

// Import Uppy styles dynamically
import('@uppy/core/dist/style.min.css').catch(err => console.warn('Could not load Uppy core CSS:', err));
import('@uppy/dashboard/dist/style.min.css').catch(err => console.warn('Could not load Uppy dashboard CSS:', err));

// Factory function to create Uppy uploader instances
export function createUppyUploader(options = {}) {
  const {
    id = 'uppy-uploader',
    show_id,
    campaign_id,
    campaign_tactic_id,
    trigger_id,
    max_file_size = 5 * 1024 * 1024, // 5MB
    allowed_file_types = ['image/*'],
    max_number_of_files = 1,
    auto_proceed = false,
    close_after_finish = true,
    max_dimensions = { width: 2000, height: 1500 },
    min_dimensions = { width: 1400, height: 950 },
    note = 'Accepted files: Various formats. Maximum size: 5MB. Please check specific requirements.',
    on_complete,
    on_error,
    alpine
  } = options;

  // Create Uppy instance
  const uppy = new Uppy({
    id: id,
    debug: false,
    autoProceed: auto_proceed,
    allowMultipleUploadBatches: false,
    restrictions: {
      maxFileSize: max_file_size,
      allowedFileTypes: allowed_file_types,
      maxNumberOfFiles: max_number_of_files
    }
  });

  // Ensure ID is set
  if (!uppy.id) {
    uppy.id = id;
  }

  // Add Dashboard plugin
  const dashboardPlugin = uppy.use(Dashboard, {
    trigger: trigger_id,
    inline: false,
    modal: true,
    width: 750,
    height: 550,
    showProgressDetails: true,
    proudlyDisplayPoweredByUppy: false,
    note: note,
    closeAfterFinish: close_after_finish,
    allowMultipleUploadBatches: false
  });

  // Add XHR upload plugin
  const xhrUpload = uppy.use(XHRUpload, {
    endpoint: campaign_tactic_id 
      ? `/shows/${show_id}/campaigns/${campaign_id}/campaign-tactics/${campaign_tactic_id}/podpromote-s3-upload`
      : `/shows/${show_id}/campaigns/${campaign_id}/podpromote-s3-upload`,
    fieldName: 'file',
    formData: true,
    headers: {
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    }
  });

  // Set up event handlers
  uppy.on('file-added', (file) => {
    console.log('File added:', file.name, 'Size:', file.size, 'Type:', file.type);
    
    // Validate file size first
    if (file.size > max_file_size) {
      const errorMessage = `File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds the maximum allowed size of ${(max_file_size / (1024 * 1024)).toFixed(2)}MB`;
      console.error('File size validation failed:', errorMessage);
      
      // Remove the file immediately to prevent further issues
      try {
        uppy.removeFile(file.id);
      } catch (removeError) {
        console.warn('Could not remove file, may already be removed:', removeError);
      }
      
      // Show error message to user
      if (alpine && typeof alpine.showUploadError === 'function') {
        alpine.showUploadError(errorMessage);
        // Also show alert for immediate feedback
        alert(errorMessage);
      } else {
        // Fallback error display
        alert(errorMessage);
      }
      return;
    }
    
    // Validate file dimensions if it's an image
    if (file.type.startsWith('image/')) {
      // Set file to processing state while validating
      try {
        uppy.setFileState(file.id, { 
          isProcessing: true 
        });
      } catch (stateError) {
        console.warn('Could not set processing state for file:', stateError);
      }

      // Create a promise to validate dimensions
      const validateDimensions = new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          if (img.width < min_dimensions.width || img.height < min_dimensions.height) {
            reject(new Error(`Image dimensions (${img.width}x${img.height}) must be at least ${min_dimensions.width}x${min_dimensions.height}px`));
          } else if (img.width > max_dimensions.width || img.height > max_dimensions.height) {
            reject(new Error(`Image dimensions (${img.width}x${img.height}) must not exceed ${max_dimensions.width}x${max_dimensions.height}px`));
          } else {
            resolve();
          }
        };
        img.onerror = () => {
          reject(new Error('Failed to load image for dimension validation'));
        };
        img.src = URL.createObjectURL(file.data);
      });

      // Handle validation result
      validateDimensions
        .then(() => {
          // Dimensions are valid, allow the file
          console.log('Image dimensions validated successfully for file:', file.id);
          
          try {
            // Just mark as not processing, let Uppy handle the rest
            uppy.setFileState(file.id, { 
              isProcessing: false 
            });
          } catch (stateError) {
            console.warn('Could not set final state for validated file:', stateError);
          }
        })
        .catch((error) => {
          // Dimensions are invalid, remove the file and show error
          console.error('Image validation failed:', error.message);
          
          // Remove the file immediately to prevent further issues
          try {
            uppy.removeFile(file.id);
          } catch (removeError) {
            console.warn('Could not remove file, may already be removed:', removeError);
          }
          
          // Show error message to user
          if (alpine && typeof alpine.showUploadError === 'function') {
            alpine.showUploadError(error.message);
          } else {
            // Fallback error display
            alert(error.message);
          }
        });
    } else {
      // For non-image files (documents), just validate file type and size
      // These are already handled by Uppy's restrictions, so we can mark as validated
      console.log('Document file added, skipping dimension validation');
      validatedFiles.add(file.id);
    }
  });

  // Handle files rejected by Uppy restrictions
  uppy.on('restriction-failed', (file, error) => {
    console.error('File restriction failed:', file, error);
    
    let errorMessage = 'File validation failed';
    if (error.code === 'FILE_SIZE_LIMIT_EXCEEDED') {
      errorMessage = `File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds the maximum allowed size of ${(max_file_size / (1024 * 1024)).toFixed(2)}MB`;
    } else if (error.code === 'INVALID_FILE_TYPE') {
      errorMessage = `File type "${file.type}" is not allowed. Accepted types: ${allowed_file_types.join(', ')}`;
    } else if (error.code === 'TOO_MANY_FILES') {
      errorMessage = `Maximum number of files (${max_number_of_files}) exceeded`;
    } else {
      errorMessage = error.message || 'File validation failed';
    }
    
    // Show error message to user
    if (alpine && typeof alpine.showUploadError === 'function') {
      alpine.showUploadError(errorMessage);
      // Also show alert for immediate feedback
      alert(errorMessage);
    } else {
      // Fallback error display
      alert(errorMessage);
    }
  });

  // Handle upload start to ensure only valid files proceed
  uppy.on('upload-start', (data) => {
    console.log('Upload starting with files:', data.fileIDs);
  });
  
  // Handle upload progress
  uppy.on('upload-progress', (file, progress) => {
    console.log('Upload progress for file:', file.id, progress.bytesUploaded, '/', progress.bytesTotal);
  });
  
  // Handle upload success
  uppy.on('upload-success', (file, response) => {
    console.log('Upload success for file:', file.id, response);
  });
  
  // Handle upload error
  uppy.on('upload-error', (file, error, response) => {
    console.error('Upload error for file:', file.id, error, response);
    
    // Show error to user
    if (alpine && typeof alpine.showUploadError === 'function') {
      alpine.showUploadError(`Upload failed: ${error.message || 'Unknown error occurred'}`);
    } else {
      alert(`Upload failed: ${error.message || 'Unknown error occurred'}`);
    }
  });

  uppy.on('complete', (result) => {
    // Call the completion callback with the full result
    if (on_complete && typeof on_complete === 'function') {
      on_complete(result);
    }
  });

  // Add global error handler for Uppy internal errors
  uppy.on('error', (error) => {
    console.error('Uppy internal error:', error);
    // Show error to user if possible
    if (alpine && typeof alpine.showUploadError === 'function') {
      alpine.showUploadError(`Upload error: ${error.message || 'Unknown error occurred'}`);
    }
  });

  // Add cleanup function to the uppy instance
  uppy.cleanup = () => {
    try {
      // Clear all files and reset state
      uppy.reset();
    } catch (error) {
      console.warn('Error during uppy cleanup:', error);
    }
  };

  return uppy;
}
